import React, { useRef, useEffect } from "react";
import "./ActionBar.css";

const ActionBar = ({ selectedCount, totalCount, entityName = "people", archivedView = false, children, hideCount = false }) => {
  const actionBarRef = useRef(null);
  
  // Smart pluralization function
  const pluralize = (word, count) => {
    if (count === 1) return word;
    
    const lowerWord = word.toLowerCase();
    
    // Handle words that are already plural
    const alreadyPlural = ['people', 'children', 'men', 'women', 'sheep', 'deer', 'fish', 'species', 'series'];
    if (alreadyPlural.includes(lowerWord)) {
      return word;
    }
    
    // Handle common irregular plurals (singular -> plural)
    const irregulars = {
      'person': 'people',
      'child': 'children',
      'tooth': 'teeth',
      'foot': 'feet',
      'man': 'men',
      'woman': 'women',
      'mouse': 'mice',
      'goose': 'geese',
    };
    
    if (irregulars[lowerWord]) {
      // Preserve original capitalization
      return word[0] === word[0].toUpperCase() 
        ? irregulars[lowerWord].charAt(0).toUpperCase() + irregulars[lowerWord].slice(1)
        : irregulars[lowerWord];
    }
    
    // If already ends with 's' or 'es' (like "companies"), return as-is
    if (word.endsWith('ies') || word.endsWith('es') || (word.endsWith('s') && word.length > 2)) {
      return word;
    }
    
    // Words ending in y (preceded by consonant) -> ies
    if (word.length > 1 && word.endsWith('y') && !/[aeiou]/.test(word[word.length - 2])) {
      return word.slice(0, -1) + 'ies';
    }
    
    // Words ending in o, x, z, ch, sh -> add es
    if (word.endsWith('o') || word.endsWith('x') || word.endsWith('z') || 
        word.endsWith('ch') || word.endsWith('sh')) {
      return word + 'es';
    }
    
    // Default: just add s
    return word + 's';
  };
  
  const pluralizedEntity = pluralize(entityName, totalCount);
  let displayText;

  if (archivedView) {
    displayText =
      selectedCount > 0
        ? `${selectedCount}/${totalCount} archived ${pluralizedEntity} selected`
        : `${totalCount} archived ${pluralizedEntity}`;
  } else {
    displayText =
      selectedCount > 0
        ? `${selectedCount}/${totalCount} ${pluralizedEntity} selected`
        : `${totalCount} ${pluralizedEntity}`;
  }

  useEffect(() => {
    const actionBar = actionBarRef.current;
    if (!actionBar) return;
    
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    
    const mouseDownHandler = (e) => {
      isDown = true;
      startX = e.pageX - actionBar.offsetLeft;
      scrollLeft = actionBar.scrollLeft;
    };
    
    const mouseLeaveHandler = () => {
      isDown = false;
    };
    
    const mouseUpHandler = () => {
      isDown = false;
    };
    
    const mouseMoveHandler = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - actionBar.offsetLeft;
      const walk = (x - startX) * 1; // adjust multiplier as needed
      actionBar.scrollLeft = scrollLeft - walk;
    };
    
    const touchStartHandler = (e) => {
      startX = e.touches[0].pageX - actionBar.offsetLeft;
      scrollLeft = actionBar.scrollLeft;
    };
    
    const touchMoveHandler = (e) => {
      const x = e.touches[0].pageX - actionBar.offsetLeft;
      const walk = x - startX;
      actionBar.scrollLeft = scrollLeft - walk;
    };
    
    actionBar.addEventListener("mousedown", mouseDownHandler);
    actionBar.addEventListener("mouseleave", mouseLeaveHandler);
    actionBar.addEventListener("mouseup", mouseUpHandler);
    actionBar.addEventListener("mousemove", mouseMoveHandler);
    actionBar.addEventListener("touchstart", touchStartHandler);
    actionBar.addEventListener("touchmove", touchMoveHandler);
    
    return () => {
      actionBar.removeEventListener("mousedown", mouseDownHandler);
      actionBar.removeEventListener("mouseleave", mouseLeaveHandler);
      actionBar.removeEventListener("mouseup", mouseUpHandler);
      actionBar.removeEventListener("mousemove", mouseMoveHandler);
      actionBar.removeEventListener("touchstart", touchStartHandler);
      actionBar.removeEventListener("touchmove", touchMoveHandler);
    };
  }, []);

  return (
    <div className="action-bar" ref={actionBarRef}>
      {!hideCount && <div className="selected-count">{displayText}</div>}
      <div className="action-controls">{children}</div>
    </div>
  );
};

export default ActionBar;
